import { NumberInput, Select } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BudgetSuggestionRow } from 'src/Components/Expenses/BudgetSuggestionRow';
import { nextDraftKey } from 'src/Components/Expenses/BudgetWizard/BudgetWizard.utils';
import useConfirmRemoveWizardLine from 'src/Components/Expenses/BudgetWizard/useConfirmRemoveWizardLine';
import {
  BudgetCategory,
  FIXED_COST_CATEGORIES,
  placementOfCategory,
} from 'src/Utils/budgetCategories';
import { amountFromVariationPercent, formatEuros } from 'src/Utils/budgetWizardMath';
import { getCategoryLabel } from 'src/Utils/expenseCategories';

import styles from './FixedCostsStep.module.scss';
import { Props } from './FixedCostsStep.types';

const FixedCostsStep = ({ previousYear, rows, onChange }: Props) => {
  const { t } = useTranslation();
  const [globalPercent, setGlobalPercent] = useState<string | number>('');
  const confirmRemove = useConfirmRemoveWizardLine('fixed_costs');

  const patchRow = (key: string, patch: Partial<(typeof rows)[number]>) =>
    onChange(rows.map(row => (row.key === key ? { ...row, ...patch } : row)));

  const applyGlobalPercent = () => {
    const parsed = typeof globalPercent === 'number' ? globalPercent : parseFloat(globalPercent);
    if (!Number.isFinite(parsed)) return;
    onChange(
      rows.map(row => {
        if (!row.checked) return row;
        // Always recomputed from N−1, never stacked on the current amount: applying +2 %
        // twice must still read +2 % over last year, not +4 %.
        const amount = amountFromVariationPercent(row.previousYearAmount, parsed);
        return amount == null ? row : { ...row, amount };
      })
    );
  };

  // Picking a category adds the line straight away: no « Ajouter » button that does nothing
  // until something is selected.
  const addCategory = (value: string | null) => {
    if (!value) return;
    const category = value as BudgetCategory;
    const placement = placementOfCategory(category);
    onChange([
      ...rows,
      {
        key: nextDraftKey('fixed'),
        category,
        mainCategory: placement.mainCategory,
        subCategory: placement.subCategory,
        amount: 0,
        previousYearAmount: null,
        note: '',
        checked: true,
        isManual: true,
      },
    ]);
  };

  const options = FIXED_COST_CATEGORIES.map(category => ({
    value: category,
    label: t(`expense.type.${category}`),
  })).sort((a, b) => a.label.localeCompare(b.label, 'fr'));

  return (
    <div className={styles.container}>
      <div className={styles.globalPercent}>
        <span className={styles.globalPercentLabel}>
          {t('budgetV2.fixedCosts.globalPercent', 'Appliquer une augmentation globale')}
        </span>
        <NumberInput
          className={styles.globalPercentInput}
          size="md"
          value={globalPercent}
          onChange={setGlobalPercent}
          onBlur={applyGlobalPercent}
          suffix=" %"
          decimalScale={1}
          placeholder="0"
          hideControls
          aria-label={t('budgetV2.fixedCosts.globalPercent', 'Appliquer une augmentation globale')}
        />
        <span className={styles.globalPercentHint}>
          {t(
            'budgetV2.fixedCosts.globalPercentHint',
            'Recalculé depuis {{year}} sur les lignes cochées.',
            { year: previousYear }
          )}
        </span>
      </div>

      <div className={styles.list}>
        {rows.length === 0 ? (
          <p className={styles.empty}>
            {t(
              'budgetV2.fixedCosts.empty',
              "Aucun frais fixe constaté l'an dernier. Ajoutez vos lignes ci-dessous."
            )}
          </p>
        ) : null}

        {rows.map(row => (
          <BudgetSuggestionRow
            key={row.key}
            checked={row.checked}
            onCheckedChange={checked => patchRow(row.key, { checked })}
            title={
              row.category
                ? t(`expense.type.${row.category}`)
                : getCategoryLabel(row.mainCategory, row.subCategory)
            }
            meta={
              row.previousYearAmount != null
                ? t('budgetV2.fixedCosts.paidIn', 'Payé en {{year}} : {{amount}}', {
                    year: previousYear,
                    amount: formatEuros(row.previousYearAmount),
                  })
                : t('budgetV2.fixedCosts.noPrevious', 'Rien constaté en {{year}}', {
                    year: previousYear,
                  })
            }
            amountLabel={t('budgetV2.amountLabel', 'Montant')}
            amount={row.amount}
            onAmountChange={amount => patchRow(row.key, { amount })}
            note={row.note}
            noteLabel={t('budgetV2.noteLabel', 'Précision')}
            notePlaceholder={t('budgetV2.notePlaceholder', 'ex. devis Dupont')}
            onNoteChange={note => patchRow(row.key, { note })}
            previousYearAmount={row.previousYearAmount}
            percentLabel={t('budgetV2.fixedCosts.versus', 'vs {{year}}', { year: previousYear })}
            onPercentChange={percent => {
              const amount = amountFromVariationPercent(row.previousYearAmount, percent);
              if (amount != null) patchRow(row.key, { amount });
            }}
            onRemove={() => {
              const label = row.category
                ? t(`expense.type.${row.category}`)
                : getCategoryLabel(row.mainCategory, row.subCategory);
              confirmRemove(label, row.amount, () => onChange(rows.filter(r => r.key !== row.key)));
            }}
            removeLabel={t('budgetV2.removeLine', 'Retirer cette ligne')}
          />
        ))}
      </div>

      <div className={styles.addRow}>
        <Select
          className={styles.addSelect}
          size="md"
          searchable
          data={options}
          value={null}
          onChange={addCategory}
          placeholder={t('budgetV2.fixedCosts.addPlaceholder', 'Choisir un frais fixe')}
          label={t('budgetV2.fixedCosts.add', 'Ajouter un frais fixe')}
        />
      </div>
    </div>
  );
};

export default FixedCostsStep;
